import type { IModuleService, ResolvedCommand } from '@hibro/shared';
import {
  BUILTIN_OPEN_COMMAND,
  BUILTIN_OPEN_SETTINGS_COMMAND,
  BUILTIN_OPEN_SETTINGS_WINDOW_COMMAND,
  MAIN_SHORTCUT_COMMAND,
} from '@hibro/shared';

/**
 * Module id the platform's own commands are attributed to. Not a real module —
 * it is what `%key%` titles resolve against (`platform:command.…`) and what a
 * face displays as the owner. `validateManifest` keeps real modules out of the
 * `workbench.` id namespace, so nothing can collide with these.
 */
export const PLATFORM_MODULE_ID = 'platform';

/** Ids of the platform's built-in commands (架构方案 §5.9). */
export const BUILTIN_COMMAND = {
  commandPalette: 'workbench.commandPalette',
  search: 'workbench.search',
  openSettings: BUILTIN_OPEN_SETTINGS_COMMAND,
  openSettingsWindow: BUILTIN_OPEN_SETTINGS_WINDOW_COMMAND,
  openLink: 'workbench.openLink',
  open: BUILTIN_OPEN_COMMAND,
  toggleFullScreen: MAIN_SHORTCUT_COMMAND.toggleFullScreen,
  reloadWindow: MAIN_SHORTCUT_COMMAND.reloadWindow,
  reloadWindowHard: MAIN_SHORTCUT_COMMAND.reloadWindowHard,
  captureRegion: 'workbench.captureRegion',
} as const;

/**
 * Declarations for the built-ins, in the same shape a module contributes — so
 * they flow through {@link queryCommands} and every presentation face without a
 * second code path. Their handlers live in the renderer (see
 * {@link withBuiltinCommands}), which is also why a sandboxed surface's reverse
 * `executeCommand` cannot reach them: those calls terminate in the extension
 * host, which has never heard of these ids.
 */
export const BUILTIN_COMMANDS: readonly ResolvedCommand[] = [
  {
    id: BUILTIN_COMMAND.commandPalette,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.commandPalette%',
    description: '%command.commandPalette.description%',
    keywords: '%command.commandPalette.keywords%',
    internal: false,
  },
  /**
   * The same box as {@link BUILTIN_COMMAND.commandPalette}, opened *without* the
   * `/` prefix.
   *
   * Two commands rather than one because the box has two modes and each needs
   * its own way in: prefilling `/` goes straight to commands and hides recent
   * searches and apps, so a single entry point had to pick one of them to make
   * unreachable by keyboard. With a chord each, the palette can prefill (which
   * is what its name always promised) and this one keeps the empty state.
   */
  {
    id: BUILTIN_COMMAND.search,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.search%',
    description: '%command.search.description%',
    keywords: '%command.search.keywords%',
    internal: false,
  },
  {
    id: BUILTIN_COMMAND.openSettings,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.openSettings%',
    description: '%command.openSettings.description%',
    keywords: '%command.openSettings.keywords%',
    internal: false,
  },
  /**
   * Open an address through the same `openLink` route a clicked link takes
   * (见 20). A renderer built-in, not a module command: `openLink` is
   * trusted-renderer only, so an ext-host handler could not call it.
   *
   * Listed (`internal: false`) because the bare command is useful — with no
   * argument it prompts for the address (见 §5.9 带参命令自行收参). A caller
   * that already has the address passes it as the first argument and skips
   * the prompt.
   */
  {
    id: BUILTIN_COMMAND.openLink,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.openLink%',
    description: '%command.openLink.description%',
    keywords: '%command.openLink.keywords%',
    internal: false,
  },
  {
    id: BUILTIN_COMMAND.openSettingsWindow,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.openSettingsWindow%',
    description: '%command.openSettingsWindow.description%',
    keywords: '%command.openSettingsWindow.keywords%',
  },
  {
    id: BUILTIN_COMMAND.open,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.open%',
    description: '%command.open.description%',
    /**
     * 不标 `internal: false`：目标是参数，裸命令打不开任何东西。用户敲的永远是
     * `/open im` 那一行——那些行由 `launch-providers` 按「此刻装了哪些模块」现合成，
     * 不是声明出来的（声明式的目标表注定漂）。
     *
     * 所以这里是「一条真命令 + 动态目标」：行有了可搬运的声明式动作（`{ command, args }` 而
     * 不是闭包），id 也就位了，`enablement` / 菜单 / 权限日后都挂得上；而命令列表里不会多出
     * 一条点了没反应的「打开」。省略即隐藏，仍显式标 `true` 是为了读声明时一眼看出这是机器入口。
     *
     * 仍然做不到的是给某个具体目标绑快捷键：`KeybindingContribution` 只有 `{ command, key,
     * when }`，没有 `args`，绑上去也只会打到裸命令。那要等键位契约补 `args`。
     */
    internal: true,
  },
  /**
   * The three the **main process** also runs (`MAIN_SHORTCUT_COMMAND`). Declared
   * here anyway — they are ordinary commands, and without a declaration the
   * shortcuts page could only show their raw id.
   *
   * Reload pair stays hidden (dev tooling); full-screen opts in — it is a
   * normal thing to look for. The **key** is still claimed by main before the
   * page sees it (见 `windowBindings` / `03` 主进程执行的窗口键), so F11 works
   * even when a WebContentsView holds focus. The **click** from the command
   * list cannot ride that path, so the renderer handler calls the same window
   * API (`windowControlsBridge.toggleFullScreen`). Leaving the handler out is
   * what made the listed row a no-op.
   */
  {
    id: BUILTIN_COMMAND.toggleFullScreen,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.toggleFullScreen%',
    description: '%command.toggleFullScreen.description%',
    runtimes: ['desktop'],
    internal: false,
  },
  {
    id: BUILTIN_COMMAND.reloadWindow,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.reloadWindow%',
    description: '%command.reloadWindow.description%',
    runtimes: ['desktop'],
    internal: true,
  },
  {
    id: BUILTIN_COMMAND.reloadWindowHard,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.reloadWindowHard%',
    description: '%command.reloadWindowHard.description%',
    runtimes: ['desktop'],
    internal: true,
  },
  {
    id: BUILTIN_COMMAND.captureRegion,
    moduleId: PLATFORM_MODULE_ID,
    category: '%command.category%',
    title: '%command.captureRegion%',
    description: '%command.captureRegion.description%',
    // Web 不提供。与全局键同一限制。
    runtimes: ['desktop'],
    internal: false,
  },
];

/**
 * Handler per built-in command id; ids without one fall through to the host.
 *
 * Handlers take the caller's arguments: a built-in may be parameterized, which
 * is what lets one declared id serve a target set decided at runtime
 * (`workbench.open <launchable>`). Dropping them here would have silently
 * turned every such call into the argument-less one.
 */
export type BuiltinCommandHandlers = Readonly<
  Partial<Record<string, (...args: unknown[]) => void>>
>;

/**
 * Decorate a module service so built-in command ids resolve in the renderer and
 * everything else still crosses to the extension host.
 *
 * Wrapping the service once — rather than teaching each call site about
 * built-ins — is what makes them reachable from every place a command can be
 * invoked: the workbench dispatcher, the activityBar, a tab controller, and any
 * view's `ViewProps.executeCommand`. All of those receive the same service
 * instance, so the single wrap covers them.
 *
 * A `Proxy` rather than an object literal because the service is itself an auto
 * RPC proxy: its members are resolved dynamically, so there is no property list
 * to spread.
 */
export function withBuiltinCommands(
  moduleService: IModuleService,
  handlers: BuiltinCommandHandlers,
): IModuleService {
  const executeCommand = (id: string, ...args: unknown[]): Promise<unknown> => {
    const handler = handlers[id];
    if (handler) {
      handler(...args);
      return Promise.resolve(undefined);
    }
    return moduleService.executeCommand(id, ...args);
  };

  return new Proxy(moduleService, {
    get(target, property, receiver) {
      if (property === 'executeCommand') {
        return executeCommand;
      }
      const value: unknown = Reflect.get(target, property, receiver);
      // Rebind methods to the real service: an RPC proxy may key its dispatch off
      // `this`, and handing it our Proxy would recurse back through this trap.
      return typeof value === 'function'
        ? (value as (...args: unknown[]) => unknown).bind(target)
        : value;
    },
  });
}
